import { cva, type VariantProps } from 'class-variance-authority';
import { Info } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { renderTrustedHtml } from '@/components/ui/render-trusted-html';
import { cn } from '@/lib/utils';

import { BANNER_TONE } from './types/banner-variants-enums';

const bannerVariants = cva(
  '@container w-full border-b px-3 py-1 text-sm/5 font-medium sm:px-4',
  {
    variants: {
      tone: {
        [BANNER_TONE.INFO]:
          'border-status-info-border bg-status-info-surface text-status-info-foreground',
        [BANNER_TONE.WARNING]:
          'border-status-warning-border bg-status-warning-surface text-status-warning-foreground',
        [BANNER_TONE.DANGER]:
          'border-status-danger-border bg-status-danger-surface text-status-danger-foreground',
        [BANNER_TONE.NEUTRAL]:
          'border-status-neutral-border bg-status-neutral-surface text-status-neutral-foreground',
      },
    },
    defaultVariants: {
      tone: BANNER_TONE.NEUTRAL,
    },
  }
);

export interface BannerRootProps
  extends ComponentProps<'div'>,
    VariantProps<typeof bannerVariants> {}

export function BannerRoot({ children, className, tone, ...props }: BannerRootProps) {
  return (
    <div
      data-slot="banner"
      className={cn(bannerVariants({ tone }), className)}
      {...props}
    >
      <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-1.5 @[420px]:gap-x-2">
        {children}
      </div>
    </div>
  );
}

export function BannerContent({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="banner-content"
      className={cn(
        'col-start-1 row-start-1 flex min-w-0 items-center gap-2 overflow-hidden @[420px]:gap-2.5',
        className
      )}
      {...props}
    />
  );
}

type BannerIconProps = Omit<ComponentProps<'span'>, 'aria-hidden'>;

export function BannerIcon({ className, ...props }: BannerIconProps) {
  return (
    <span
      aria-hidden="true"
      data-slot="banner-icon"
      className={cn(
        'mt-0.5 flex size-4 shrink-0 items-center justify-center [&_svg]:size-4',
        className
      )}
      {...props}
    />
  );
}

export function BannerMessage({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="banner-message"
      className={cn(
        'min-w-0 flex-1 truncate whitespace-nowrap text-sm/5 font-medium',
        className
      )}
      {...props}
    />
  );
}

export function BannerActions({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="banner-actions"
      className={cn(
        'col-start-2 row-start-1 flex shrink-0 items-center',
        className
      )}
      {...props}
    />
  );
}

export function BannerDismiss({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      data-slot="banner-dismiss"
      className={cn('col-start-3 row-start-1 shrink-0', className)}
      {...props}
    />
  );
}

interface BannerActionLabelProps {
  compact: ReactNode;
  children: ReactNode;
}

export function BannerActionLabel({ compact, children }: BannerActionLabelProps) {
  return (
    <>
      <span className="@[420px]:hidden">{compact}</span>
      <span className="hidden @[420px]:inline">{children}</span>
    </>
  );
}

// ---------------------------------------------------------------------------
// Legacy InfoBanner — PERMANENT intentional exception for destination field
// `component_type: html` strings (manage-destination-field-wrapper). Shell
// Banner is full-bleed chrome + ReactNode; do not blind-migrate HTML markup.
// ---------------------------------------------------------------------------

interface InfoBannerProps {
  message: string;
  links?: {
    text: string;
    href: string;
  }[];
  details?: ReactNode;
  className?: string;
}

export function InfoBanner({
  message,
  links: _links = [],
  details: _details,
  className = '',
}: InfoBannerProps) {
  return (
    <div
      className={`flex items-center gap-3 rounded-md bg-status-warning/10 p-5 text-foreground ${className}`}
    >
      <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-status-warning text-white">
        <Info className="my-auto h-4 w-4" />
      </div>
      <div>
        <p className="text-sm font-normal text-foreground/90 [&>a]:font-semibold [&>a]:underline">
          {renderTrustedHtml(message)}
        </p>
      </div>
    </div>
  );
}
